import axios, { isAxiosError } from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { getCustomerChatTurnTimeoutMs } from "../utils/customerChatTurnTimeoutMs";
import { normalizeOutgoingChatMessage } from "../utils/normalizeOutgoingChatMessage";

function post(path, body, opts = {}) {
  return axios.post(resolveApiUrl(path), body, {
    withCredentials: Boolean(opts.withCredentials),
    timeout: opts.timeout ?? 180000,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * @param {unknown} err
 * @param {{ op?: string, sessionId?: string, path?: string }} ctx
 */
function logCustomerChatPublicAxiosError(err, ctx = {}) {
  const isAx = isAxiosError(err);
  const canceled =
    typeof axios.isCancel === "function" ? axios.isCancel(err) : false;
  const code = err && typeof err === "object" ? err.code : undefined;
  const message = err && typeof err === "object" ? err.message : String(err);
  const response = isAx ? err.response : undefined;
  const status = response?.status;
  const data = response?.data;
  const responseText =
    response?.data != null && typeof response.data !== "object"
      ? String(response.data)
      : null;
  const req = isAx ? err.request : undefined;
  const hasResponse = Boolean(response);
  const isTimeout =
    code === "ECONNABORTED" || /timeout/i.test(String(message || ""));
  /** @type {"timeout"|"canceled"|"response_error"|"no_response"|"non_axios_error"} */
  let kind = "non_axios_error";
  if (canceled) kind = "canceled";
  else if (isTimeout) kind = "timeout";
  else if (hasResponse) kind = "response_error";
  else if (isAx) kind = "no_response";

  const requestSummary =
    req && typeof req === "object"
      ? {
          readyState: req.readyState,
          status: req.status,
          responseURL: req.responseURL,
        }
      : null;

  // eslint-disable-next-line no-console
  console.error("[customer-chat-public] axios error", {
    kind,
    isAxiosError: isAx,
    canceled,
    code,
    message,
    responseStatus: status,
    responseData: data,
    responseTextSnippet:
      responseText != null && responseText.length > 800
        ? `${responseText.slice(0, 800)}…`
        : responseText,
    requestSummary,
    ...ctx,
  });
}

/**
 * @param {{ ref: string, locale?: string, customer_identifier?: string }} params
 */
export async function publicStartCustomerChat(params) {
  const _t0 = Date.now();
  try {
    const { data } = await post(
      "/api/customer-chat/public/start",
      {
        ref: params.ref,
        locale: params.locale || "he",
        customer_identifier: params.customer_identifier,
      },
      { timeout: 60000 }
    );
    if (data?.status === "fail") {
      const err = new Error(data?.message || "public_chat_start_failed");
      err.customerChatPublicPayload = data;
      throw err;
    }
    return data;
  } catch (e) {
    const d = e?.response?.data;
    if (d && d.status === "fail" && typeof d.message === "string" && d.message.trim()) {
      const err = new Error(d.message.trim());
      err.customerChatPublicPayload = d;
      throw err;
    }
    throw e;
  }
}

export async function publicSendCustomerChatTurn(sessionId, messageText, metadata) {
  const msg = normalizeOutgoingChatMessage(messageText);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info("[customer-chat-public] sendTurn", {
      sessionId,
      rawType: typeof messageText,
      message_text: msg,
    });
  }
  try {
    const { data } = await post(
      "/api/customer-chat/public/turn",
      {
        session_id: sessionId,
        message_text: msg,
        metadata: metadata || {},
      },
      { timeout: getCustomerChatTurnTimeoutMs() }
    );
    if (data?.status === "fail") {
      const err = new Error(data?.message || "public_chat_turn_failed");
      err.customerChatPublicPayload = data;
      throw err;
    }
    return data;
  } catch (e) {
    if (isAxiosError(e)) {
      logCustomerChatPublicAxiosError(e, {
        op: "public_turn",
        sessionId,
        path: "/api/customer-chat/public/turn",
      });
    }
    throw e;
  }
}

export async function listPublicCustomerChatMessages(sessionId, limit = 200) {
  const _t0 = Date.now();
  const { data } = await post(
    "/api/customer-chat/public/messages",
    { session_id: sessionId, limit },
    { timeout: 60000 }
  );
  if (data?.status === "fail") {
    const err = new Error(data?.message || "public_chat_messages_failed");
    err.customerChatPublicPayload = data;
    throw err;
  }
  return data;
}

export function publicChatSessionStorageKey(ref) {
  return `hr_cc_pub_v1_${String(ref || "").trim()}`;
}
